import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useCountUp } from "@/shared/hooks/use-count-up";
import { useGsapReveal } from "@/shared/hooks/use-gsap-reveal";
import { Link } from "@tanstack/react-router";
import {
  Building2,
  Globe,
  GraduationCap,
  Lightbulb,
  MapPin,
  ShieldCheck,
  TrendingUp,
  Wrench,
} from "lucide-react";
import { CertificatesGallery } from "./certificates-gallery";

const BELIEFS = [
  {
    icon: ShieldCheck,
    title: "Výsledky bez chaosu",
    text: "Radikální reorganizace nefungují. Efektivní změna probíhá postupně — krok za krokem a v tempu, které vaše firma zvládne.",
  },
  {
    icon: Wrench,
    title: "Praxe mluví hlasitěji než teorie",
    text: "Každý nástroj, který vás naučíme, jsme sami úspěšně otestovali v reálném byznysu.",
  },
  {
    icon: Lightbulb,
    title: "Aha-momenty s okamžitým dopadem",
    text: "Někdy stačí jediná správná myšlenka nebo nový pohled na problém, aby odstartovala pozitivní proměnu celé organizace.",
  },
  {
    icon: TrendingUp,
    title: "Růst bez ohledu na startovní čáru",
    text: "Posunout se může každá firma i jednotlivec. Nezáleží na velikosti vašeho byznysu, ale na chuti se zlepšovat.",
  },
] as const;

/** Statický About-us obsah — textace dle podkladů klienta (dokument „O Nás návrhy"). */
export function AboutPage() {
  const statsRef = useGsapReveal<HTMLDivElement>({
    childrenSelector: "[data-stat-card]",
    stagger: 0.1,
  });
  const proseRef = useGsapReveal<HTMLDivElement>({ y: 30 });
  const beliefsRef = useGsapReveal<HTMLDivElement>({
    childrenSelector: "[data-belief]",
    stagger: 0.08,
  });
  const yearsRef = useCountUp({ target: 10, suffix: "+" });
  const graduatesRef = useCountUp({ target: 500, suffix: "+" });

  return (
    <div className="mx-auto max-w-6xl px-4 py-16" data-testid="hca-about-page">
      <div className="max-w-3xl">
        <div className="flex items-center gap-3">
          <span aria-hidden="true" className="h-px w-8 bg-[color:var(--hca-gold)]" />
          <div className="font-semibold text-[color:var(--hca-gold)] text-sm uppercase tracking-[0.22em]">
            O nás
          </div>
        </div>
        <h1 className="mt-3 mb-4 text-4xl font-bold tracking-tight">O HCA Moravia</h1>
        <p className="mb-5 text-xl font-semibold leading-snug text-foreground">
          Pomáháme majitelům firem rozhodovat s větším klidem a řídit firmu s větším přehledem.
        </p>
        <p className="text-lg text-muted-foreground">
          Pomáháme majitelům, CEO a manažerským týmům dostat firmu pod větší kontrolu — v řízení
          lidí, obchodu, komunikaci i každodenním rozhodování. Nejsme masové školení: pracujeme v
          malých skupinách, prakticky a přímo na situacích, které firmy skutečně řeší.
        </p>
      </div>

      <div ref={statsRef} className="mt-10 mb-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card data-stat-card data-testid="hca-about-stat-years">
          <CardContent className="p-5 text-center">
            <GraduationCap className="mx-auto mb-2 h-8 w-8 text-primary" aria-hidden="true" />
            <div className="text-3xl font-bold">
              <span ref={yearsRef}>10+</span>
            </div>
            <div className="text-sm text-muted-foreground">let zkušeností</div>
          </CardContent>
        </Card>
        <Card data-stat-card data-testid="hca-about-stat-graduates">
          <CardContent className="p-5 text-center">
            <Building2 className="mx-auto mb-2 h-8 w-8 text-primary" aria-hidden="true" />
            <div className="text-3xl font-bold">
              <span ref={graduatesRef}>500+</span>
            </div>
            <div className="text-sm text-muted-foreground">absolventů ročně</div>
          </CardContent>
        </Card>
        <Card data-stat-card data-testid="hca-about-stat-network">
          <CardContent className="p-5 text-center">
            <Globe className="mx-auto mb-2 h-8 w-8 text-primary" aria-hidden="true" />
            <div className="text-3xl font-bold">~80</div>
            <div className="text-sm text-muted-foreground">center v mezinárodní síti</div>
          </CardContent>
        </Card>
        <Card data-stat-card data-testid="hca-about-stat-location">
          <CardContent className="p-5 text-center">
            <MapPin className="mx-auto mb-2 h-8 w-8 text-primary" aria-hidden="true" />
            <div className="text-3xl font-bold">Brno</div>
            <div className="text-sm text-muted-foreground">kanceláře, Úlehlova 20a</div>
          </CardContent>
        </Card>
      </div>

      <div ref={proseRef} className="prose prose-slate max-w-3xl dark:prose-invert">
        <h2 className="mt-8 text-2xl font-semibold">
          Stavíme firmy na pevné základy. Bez teoretizování, krok za krokem.
        </h2>
        <p className="text-muted-foreground">
          Jako majitel, CEO nebo manažer denně čelíte obrovské odpovědnosti. Řídit firmu, vést lidi
          a zajišťovat stabilní růst je komplexní disciplína — a my víme, že i ten nejvyšší šéf
          občas potřebuje partnera, se kterým se může poradit.
        </p>

        <h2 className="mt-8 text-2xl font-semibold">Globální know-how s lokálními výsledky</h2>
        <p className="text-muted-foreground">
          Navazujeme na dlouholetou práci a odkaz Mgr. Martina Koleničky a stavíme na celosvětově
          uznávané manažerské technologii Hubbard College of Administration. Jsme součástí
          mezinárodní sítě přibližně 80 vzdělávacích center po celém světě a tuto osvědčenou
          technologii adaptujeme na specifika českého a slovenského trhu. Nepřednášíme akademickou
          teorii — předáváme funkční nástroje, které okamžitě zvýší efektivitu, produktivitu a
          stabilitu vaší firmy.
        </p>
        <figure className="mt-6" data-testid="hca-about-thermoservis-photo">
          <img
            src="/hca-about/thermoservis-vyroci.jpg"
            alt="Ondřej Křelina s majitelem firmy Thermoservis na oslavě 30 let firmy"
            className="w-full rounded-lg border border-border object-cover"
            loading="lazy"
          />
          <figcaption className="mt-2 text-muted-foreground text-sm">
            S klienty jsme i ve chvílích, kdy se slaví — výročí 30 let firmy Thermoservis.
          </figcaption>
        </figure>
      </div>

      <section className="mt-12" data-testid="hca-about-beliefs">
        <h2 className="font-bold text-2xl tracking-tight sm:text-3xl">V co věříme</h2>
        <div ref={beliefsRef} className="mt-6 grid gap-4 sm:grid-cols-2">
          {BELIEFS.map((b) => (
            <div
              key={b.title}
              data-belief
              className="rounded-lg border border-[color:var(--hca-gold)]/25 bg-card p-5 transition hover:border-[color:var(--hca-gold)]/50"
            >
              <b.icon className="h-6 w-6 text-[color:var(--hca-gold)]" aria-hidden="true" />
              <div className="mt-3 font-semibold text-foreground">{b.title}</div>
              <p className="mt-1 text-muted-foreground text-sm leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      <CertificatesGallery />

      <Card
        className="mt-12 max-w-3xl border-[color:var(--hca-gold)]/30 bg-card"
        data-testid="hca-about-leadership"
      >
        <CardContent className="p-6">
          <div className="font-semibold text-[color:var(--hca-gold)] text-sm uppercase tracking-[0.22em]">
            Kdo stojí v čele HCA Moravia
          </div>
          <div className="mt-4 flex flex-col gap-5 sm:flex-row sm:items-start">
            <img
              src="/hca-team/ondra.jpg"
              alt="Ondřej Křelina — majitel a ředitel HCA Moravia"
              className="h-24 w-24 flex-shrink-0 rounded-full object-cover sm:h-28 sm:w-28"
              loading="lazy"
            />
            <div className="min-w-0">
              <div className="text-2xl font-bold tracking-tight">Ondřej Křelina</div>
              <div className="text-sm text-muted-foreground">Majitel a ředitel společnosti</div>
              <blockquote className="mt-4 border-[color:var(--hca-gold)] border-l-2 pl-4 text-lg italic text-foreground">
                „Za 18 let v podnikání a 13 let v oblasti manažerského vzdělávání jsem zjistil
                jedno: velké změny nezačínají složitými procesy, ale správným rozhodnutím ve správný
                čas."
              </blockquote>
              <p className="mt-4 text-muted-foreground">
                Ondřej spojuje vlastní podnikatelské zkušenosti s přesnými nástroji Hubbard College
                of Administration. Pomáhá majitelům firem a jejich týmům odstraňovat chaos z řízení
                a budovat stabilní, samostatné a dlouhodobě profitabilní organizace.
              </p>
            </div>
          </div>
          <div className="mt-6 flex flex-col gap-3 border-border border-t pt-5 sm:flex-row sm:items-center">
            <p className="font-semibold text-foreground">
              Chcete posunout své řízení na novou úroveň?
            </p>
            <Button asChild>
              <Link to="/diagnoza" data-testid="hca-about-cta-diagnoza">
                Nezávazně zkonzultovat situaci
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="mt-10 max-w-3xl text-sm text-muted-foreground">
        <h2 className="mb-1 font-semibold text-base text-foreground">Právní údaje</h2>
        <p>
          Fakturační subjekt: <strong className="text-foreground">HCA Czechia s.r.o.</strong>, IČO
          23530936, DIČ CZ23530936, se sídlem V aleji 238/57, Holásky, 620 00 Brno. Detaily najdete
          v{" "}
          <a href="/gdpr" className="text-primary underline">
            GDPR dokumentu
          </a>
          .
        </p>
      </div>
    </div>
  );
}
